import * as React from 'react';
import {useTheme, tokens} from '@packages/utilities/styled';
import {AddIcon} from '@packages/systems/spring';
import {onButtonConfirm} from '@packages/utilities/compose-events';

// @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
const notNull = (x) => x != null;

export type SymbolBindingDotProps = {
  keyboardFocus?: boolean;
  /** This component does not accept children */
  children?: undefined;
  active?: boolean;
  inline?: boolean;
  hover?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  /** Whether this is a clickable version */
  clickable?: boolean;
  style?: React.CSSProperties &
    Partial<{
      /** Do not specify display - for inline behavior use the `inline` prop */
      display: never;
    }>;
  tabIndex?: number;
};

export const SymbolBindingDot = React.forwardRef(
  (
    {
      keyboardFocus,
      style,
      active,
      inline,
      hover,
      clickable,
      disabled,
      onClick,
      onKeyDown,
      tabIndex,
      ...rest
    }: SymbolBindingDotProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const {gutters, colors, control, focusColors, shadows} = useTheme();
    const hoverTransform = inline ? 'scale(1.5)' : 'scale(1.75)';
    const hovered = hover && !disabled;
    // @ts-expect-error -- the type of the onClick prop is not compatible with the type of the onButtonConfirm function since it uses an inline Event definition
    const keyboardHandlers = onButtonConfirm(onClick);

    return (
      <div
        ref={ref}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                onKeyDown && onKeyDown(e);
                keyboardHandlers.onKeyDown(e);
              }
        }
        onKeyUp={disabled ? undefined : keyboardHandlers.onKeyUp}
        onClick={disabled ? undefined : onClick}
        tabIndex={tabIndex ?? 0}
        {...rest}
        aria-disabled={disabled}
        role="button"
        style={{
          ...style,
          width: inline ? gutters.base * 3 : gutters.base * 5,
          height: inline ? gutters.base * 3 : gutters.base * 5,
          display: inline ? 'inline-flex' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'default',
          opacity: disabled ? 0.6 : 1,
          // Prevents default focus-visible styling since we handle the focus in
          // the wrapped element:
          outline: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            transform: hovered ? hoverTransform : 'scale(1)',
            width: gutters.base * 3,
            height: gutters.base * 3,
            borderColor: inline ? colors.background : colors.backgroundDeep,
            borderWidth: control.borderWidth,
            borderStyle: control.borderStyle,
            borderRadius: 9999,
            backgroundColor: colors.dynamicBackgroundAlt,
            color: tokens.colors.white,
            filter: active ? 'brightness(0.75)' : undefined,
            transitionProperty: 'transform',
            transitionDuration: '60ms',
            transitionTimingFunction: 'ease-out',
            boxShadow: [
              keyboardFocus ? focusColors.outline : null,
              clickable ? shadows.medium : null,
              hover && !inline ? shadows.large : null,
            ]
              .filter(notNull)
              .join(', '),
          }}
        />

        <div
          style={{
            position: 'absolute',
            opacity: hovered ? 1 : 0,
            color: tokens.colors.white,
            transform: hovered ? 'scale(1)' : 'scale(0.5)',
            transitionProperty: 'transform',
            transitionDuration: '60ms',
            transitionTimingFunction: 'ease-out',
          }}
        >
          <AddIcon size={10} />
        </div>

        <div
          style={{
            position: 'absolute',
            width: gutters.base,
            height: gutters.base,
            borderRadius: 9999,
            backgroundColor: colors.white,
            opacity: hovered ? 0 : 1,
            transitionProperty: 'opacity',
            transitionDuration: '60ms',
            transitionTimingFunction: 'linear',
          }}
        />
      </div>
    );
  }
);
